/**
 * What the API accepts for a sponsor logo.
 *
 * Mirrors `readLogoUpload` in the API's `src/admin/event/event.services.js`, so
 * a file that will be refused is refused here first — the API says the same
 * thing, but only after the whole file has crossed the wire.
 *
 * The upload field is called `logo`. `icon` and `image` are still accepted as
 * aliases, but every response calls it a logo, so that is the name to send.
 */

export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

export const LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
  "image/gif",
];

/** For an `<input type="file" accept>`. */
export const LOGO_ACCEPT = LOGO_TYPES.join(",");

/** Human-readable version of the same rule, for helper text. */
export const LOGO_HINT = "PNG, JPG, WebP, SVG or GIF up to 2MB";

/**
 * Why this file cannot be used as a logo, or null if it can.
 *
 * @param {File} file
 * @returns {string|null}
 */
export const logoFileError = (file) => {
  if (!file) return null;

  if (!LOGO_TYPES.includes(String(file.type).toLowerCase())) {
    return `A logo must be a PNG, JPEG, WebP, SVG or GIF — got ${file.type || "no type"}`;
  }
  if (file.size > LOGO_MAX_BYTES) {
    return `That logo is ${(file.size / (1024 * 1024)).toFixed(1)}MB — the limit is 2MB`;
  }
  return null;
};

/**
 * Is this icon URL worth sending back to the API?
 *
 * The API stores a relative path (`event-sponsor/abc.png`) but sends an
 * absolute one (`https://…/storage/event-sponsor/abc.png`). Echoing back what
 * it just sent would write the absolute URL into the column, and the next
 * response would prefix it a second time — a logo that survives one save and
 * breaks on the next.
 *
 * So an untouched URL is left out of the payload entirely, which the API reads
 * as "leave the logo alone". Only a URL somebody actually edited is sent, and
 * clearing the field still sends "" so a logo can be removed on purpose.
 */
export const iconUrlChanged = (current, original) =>
  String(current || "").trim() !== String(original || "").trim();

/**
 * Pull an image out of a paste, or null when the clipboard held none.
 *
 * A screenshot or an image copied from another page arrives on the clipboard as
 * a file item, not as text — so pasting one into a plain URL box otherwise does
 * nothing at all, with no hint as to why. Anything that is not an image is left
 * alone so ordinary text pasting still works.
 */
export const imageFromClipboard = (event) => {
  const items = event?.clipboardData?.items;
  if (!items) return null;

  for (const item of items) {
    if (item.kind === "file" && String(item.type).toLowerCase().startsWith("image/")) {
      const file = item.getAsFile();
      if (file) return namedImageFile(file);
    }
  }
  return null;
};

/**
 * Give a pasted image a real filename.
 *
 * The clipboard hands over a file that browsers name "image.png" or, in some,
 * "blob" or nothing at all. A name with no extension is what a multipart upload
 * sends as the filename, so it is worth fixing before it reaches the API.
 */
export const namedImageFile = (file) => {
  if (!file) return null;

  const name = String(file.name || "");
  const looksNamed = name && name !== "blob" && /\.[a-z0-9]+$/i.test(name);
  if (looksNamed) return file;

  // "image/svg+xml" -> "svg", "image/jpeg" -> "jpeg"
  const extension = String(file.type || "image/png")
    .split("/")[1]
    .replace(/\+.*$/, "");

  return new File([file], `pasted-logo-${Date.now()}.${extension}`, { type: file.type });
};
